/**
 * API client for Splyt backend
 * Handles all API requests with authentication
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { getTelegramInitData } from './telegram';
import { isDevModeEnabled } from './devMode';
import { mockItems, devUser, searchMock, getSimilarItems, getCreatorsByRole } from './mocks';

// API base URL - configured via environment variable
// Set VITE_API_URL in your .env file or Netlify environment variables
// Examples:
//   - Railway: https://your-app-production.up.railway.app/api
//   - Render: https://your-app.onrender.com/api
//   - Your server: https://api.yourdomain.com/api
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const initData = getTelegramInitData();
    if (initData) {
      config.headers['X-Telegram-Init-Data'] = initData;
    }
    
    // Add JWT token if available
    const token = localStorage.getItem('splyt_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to auth
      localStorage.removeItem('splyt_token');
    }
    return Promise.reject(error);
  }
);

// ============= TYPES =============

export interface User {
  id: number;
  telegram_id: number;
  username?: string;
  first_name: string;
  last_name?: string;
  language: string;
  is_creator: boolean;
  created_at: string;
  avatar?: string; // Add avatar field
}

// Profile & referral/boost types
export type RoleKey = 'listener' | 'beatmaker' | 'producer' | 'designer' | 'performer' | 'director';

export interface PortfolioItem {
  type: 'link' | 'photo' | 'video' | 'audio';
  value: string;
  title?: string;
  description?: string;
  price_uah?: number | null;
  cover_url?: string | null;
  preview_url?: string | null;
  category?: string | null;
  item_id?: string | number | null;
  source?: 'marketplace' | 'portfolio';
  created_at?: string | null;
  link?: string | null;
  creator_id?: number;
  id?: string | number;
}

export type ContactTelegramMode = 'hidden' | 'username' | 'custom';
export type ContactTelegramPrivacy = 'no' | 'ask' | 'liked_only' | 'everyone';

export interface UserProfile {
  telegram_id: number;
  username?: string;
  name?: string;
  photo_file_id?: string | null;
  roles: string; // comma-separated role keys
  city: string;
  styles: string;
  bio: string;
  portfolios: PortfolioItem[];
  is_boosted?: boolean;
  boost_expires_at?: string | null;
  referral_count?: number;
  contact_telegram_mode?: ContactTelegramMode;
  contact_telegram_custom?: string | null;
  contact_telegram_privacy?: ContactTelegramPrivacy;
  // In-app chat privacy
  allow_chat_messages?: boolean;
  social_links?: {
    instagram?: string;
    soundcloud?: string;
    spotify?: string;
    youtube?: string;
  };
}

export type ChatApprovalMode = 'auto' | 'manual';

export interface ChatApprovalStatus {
  mode: ChatApprovalMode;
  approved: boolean;
}

export interface MarketProfile extends UserProfile {
  stats?: {
    marketplace_items: number;
    portfolio_items: number;
    // Engagement stats (may be absent on older backends)
    total_likes?: number;
    profile_likes?: number;
    item_likes?: number;
    followers?: number;
  };
}

// Extended profile with parsed roles for UI components
export type ParsedUserProfile = Omit<UserProfile, 'roles'> & {
  roles: string[]; // parsed roles array
  stats?: MarketProfile['stats'];
};

export interface CandidateProfile {
  telegram_id: number;
  username?: string;
  name?: string;
  roles: string[];
  roles_display?: string[];
  city?: string;
  styles: string[];
  bio?: string;
  link?: string;
  photo_url?: string;
  photo_file_id?: string | null;
  is_boosted?: boolean;
  portfolios: PortfolioItem[];
  elo_rating?: number;
  total_likes_received?: number;
  total_matches?: number;
  last_activity_at?: string | null;
}

export interface Item {
  id: string;
  creator_id: number;
  category: 'beat' | 'cover' | 'feature' | 'mix' | 'kit' | 'service';
  title: string;
  description: string;
  price_uah: number;
  tags: string[];
  bpm?: number;
  key?: string;
  formats: Array<{ id: string; price: number }>;
  preview_url: string;
  media_url: string;
  cover_url: string;
  is_exclusive: boolean;
  status: 'draft' | 'pending' | 'active' | 'rejected' | 'sold_out';
  created_at: string;
  creator?: {
    id: number;
    name: string;
    username?: string;
    avatar?: string;
  };
  stats?: {
    likes: number;
    plays: number;
  };
}

export interface AuthResponse {
  ok: boolean;
  token: string;
  user: User;
}

export interface SwipesResponse {
  data: CandidateProfile[];
  swipes_used: number;
  swipes_remaining: number;
}

export interface CatalogResponse {
  data: Item[];
  total: number;
  page: number;
  per_page: number;
}

export interface PurchaseRequest {
  item_id: string;
  format: string;
  return_chat_id: number;
}

export interface PurchaseResponse {
  ok: boolean;
  invoice_id: string;
  telegram_invoice_params: Record<string, unknown>;
}

export interface PurchaseFile {
  url: string;
  name?: string | null;
  type?: string | null;
}

export interface UserPurchase {
  id: number;
  item_id: string;
  format: string;
  price_paid: number;
  status: string;
  created_at: string;
  title?: string;
  cover_url?: string | null;
  files: PurchaseFile[];
}

export interface CreateWorkMedia {
  file: File;
}

export interface CreateWorkData {
  title: string;
  description: string;
  tags: string[];
  price: string;
  category: string;
  bpm?: string;
  key?: string;
  /**
   * Legacy single-step media upload (existing miniapp behaviour).
   * When any of the new fields below is provided, the backend will use the new upload model instead.
   */
  media?: CreateWorkMedia[];
  /**
   * New multi-step upload model fields.
   * These map to backend fields: preview_file, cover_file, deliverable_files, delivery_formats.
   */
  previewFile?: File | null;
  coverFile?: File | null;
  deliverableFiles?: File[];
  deliveryFormats?: string[];
}

export interface CreateWorkResponse {
  status: string;
  item_id: number | string;
}

// KYC verification types (stub backend)
export type KycStatus = 'unverified' | 'pending' | 'verified' | 'rejected';

export interface KycVerification {
  id: string;
  user_id: string;
  status: KycStatus;
  created_at: string;
  updated_at: string;
  has_document_image: boolean;
  has_selfie_image: boolean;
  face_match_score?: number | null;
  liveness_score?: number | null;
  reason?: string | null;
}

export interface UploadPortfolioFileResponse {
  url: string;
  caption: string;
  type: string;
}

export type AnalyticsEventType = 'view' | 'play' | 'search_click';

export interface RecordInteractionPayload {
  itemId: string;
  eventType: AnalyticsEventType;
  durationMs?: number | null;
}

// ============= API METHODS =============

export const realApi = {
  // Auth
  auth: {
    init: async (): Promise<AuthResponse> => {
      const response = await apiClient.post('/auth/init', {
        init_data: getTelegramInitData(),
      });
      
      // Save token
      if (response.data.token) {
        localStorage.setItem('splyt_token', response.data.token);
      }
      
      return response.data;
    },
  },
  
  // Swipes
  swipes: {
    getForYou: async (
      telegramId: number,
      options?: {
        limit?: number;
        filterRoles?: string[];
        filterCity?: string;
        filterStyles?: string[];
      }
    ): Promise<SwipesResponse> => {
      const response = await apiClient.post('/get_candidates', {
        telegram_id: telegramId,
        limit: options?.limit,
        filter_roles: options?.filterRoles,
        filter_city: options?.filterCity,
        filter_styles: options?.filterStyles,
      });
      return response.data;
    },
    
    like: async (
      fromUserId: number,
      toUserId: number
    ): Promise<{ ok: boolean; is_match: boolean; swipes_used: number; swipes_remaining: number }> => {
      const response = await apiClient.post('/like', {
        from_user_id: fromUserId,
        to_user_id: toUserId,
      });
      return response.data;
    },
    
    skip: async (
      fromUserId: number,
      toUserId: number
    ): Promise<{ ok: boolean; swipes_used: number; swipes_remaining: number }> => {
      const response = await apiClient.post('/skip', {
        from_user_id: fromUserId,
        to_user_id: toUserId,
      });
      return response.data;
    },
  },
  
  // Catalog
  catalog: {
    getItems: async (params?: {
      category?: string;
      sort?: string;
      cursor?: string;
      filters?: Record<string, unknown>;
    }): Promise<CatalogResponse> => {
      const response = await apiClient.get('/catalog', { params });
      return response.data;
    },
    
    getItem: async (id: string): Promise<Item> => {
      const response = await apiClient.get(`/item/${id}`);
      return response.data;
    },

    create: async (data: CreateWorkData): Promise<CreateWorkResponse> => {
      const formData = new FormData();

      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('price', data.price);
      formData.append('tags', data.tags.join(','));
      formData.append('category', data.category);

      if (data.bpm) {
        formData.append('bpm', data.bpm);
      }

      if (data.key) {
        formData.append('key', data.key);
      }

      const hasNewMode =
        !!data.previewFile ||
        !!data.coverFile ||
        (data.deliverableFiles && data.deliverableFiles.length > 0) ||
        (data.deliveryFormats && data.deliveryFormats.length > 0);

      if (hasNewMode) {
        // New multi-step upload model
        if (data.deliveryFormats && data.deliveryFormats.length > 0) {
          formData.append('delivery_formats', data.deliveryFormats.join(','));
        }

        if (data.previewFile) {
          formData.append('preview_file', data.previewFile, data.previewFile.name);
        }

        if (data.coverFile) {
          formData.append('cover_file', data.coverFile, data.coverFile.name);
        }

        if (data.deliverableFiles && data.deliverableFiles.length > 0) {
          data.deliverableFiles.forEach((file) => {
            formData.append('deliverable_files', file, file.name);
          });
        }
      } else if (data.media && data.media.length > 0) {
        // Legacy single-step behaviour (backwards compatible)
        data.media.forEach((mediaFile) => {
          if (mediaFile?.file) {
            formData.append('media_files', mediaFile.file, mediaFile.file.name);
          }
        });
      } else {
        // Neither new nor legacy files provided – let backend handle validation error
      }

      const response = await apiClient.post('/catalog/create', formData);

      return response.data;
    },
  },
  
  // Marketplace profiles
  market: {
    getProfile: async (userId: number): Promise<MarketProfile> => {
      const response = await apiClient.get(`/market/profile/${userId}`);
      return response.data;
    },
  },

  // Search
  search: {
    query: async (q: string, filters?: Record<string, unknown>): Promise<{
      data: Item[];
      corrected_query?: string;
      original_query: string;
      related_queries: string[];
      similar_items: Item[];
    }> => {
      const response = await apiClient.get('/search', {
        params: { q, ...filters },
      });
      return response.data;
    },
    suggestions: async (q: string): Promise<{ suggestions: string[] }> => {
      const response = await apiClient.get('/search/suggestions', {
        params: { q },
      });
      return response.data;
    },
  },
  
  // Similar items
  similar: {
    get: async (itemId: string, limit?: number): Promise<CatalogResponse> => {
      const response = await apiClient.get(`/item/${itemId}/similar`, {
        params: { limit },
      });
      return response.data;
    },
  },
  
  // Recommendations
  recommendations: {
    get: async (limit?: number): Promise<{
      data: Item[];
      cached: boolean;
      total: number;
    }> => {
      const response = await apiClient.get('/v1/recommendations', {
        params: { limit },
      });
      return response.data;
    },
  },

  // Analytics
  analytics: {
    recordInteraction: async (payload: RecordInteractionPayload): Promise<{ status: string }> => {
      const response = await apiClient.post('/analytics/record_interaction', {
        item_id: payload.itemId,
        event_type: payload.eventType,
        duration_ms: payload.durationMs ?? null,
      });

      return response.data;
    },
  },
  
  // User actions
  user: {
    follow: async (userId: number): Promise<{ ok: boolean }> => {
      const response = await apiClient.post(`/user/${userId}/follow`);
      return response.data;
    },
    
    unfollow: async (userId: number): Promise<{ ok: boolean }> => {
      const response = await apiClient.post(`/user/${userId}/unfollow`);
      return response.data;
    },

    getFollowStatus: async (userId: number): Promise<{ is_following: boolean }> => {
      const response = await apiClient.get(`/user/${userId}/follow-status`);
      return response.data;
    },
    
    favorite: async (itemId: string): Promise<{ ok: boolean; favorited: boolean }> => {
      const response = await apiClient.post(`/user/favorite`, { item_id: itemId });
      return response.data;
    },
    
    getFavorites: async (): Promise<Item[]> => {
      const response = await apiClient.get('/user/favorites');
      return response.data;
    },
    
    getDashboard: async (): Promise<unknown> => {
      const response = await apiClient.get('/user/dashboard');
      return response.data;
    },

    getPurchases: async (): Promise<UserPurchase[]> => {
      const response = await apiClient.get('/my_purchases');
      const data = (response.data && (response.data as { data?: unknown }).data) || [];
      return Array.isArray(data) ? (data as UserPurchase[]) : [];
    },
    
    block: async (userId: number): Promise<{ ok: boolean }> => {
      const response = await apiClient.post('/user/block', {
        blocked_user_id: userId,
      });
      return response.data;
    },
    
    unblock: async (userId: number): Promise<{ ok: boolean }> => {
      const response = await apiClient.post('/user/unblock', {
        blocked_user_id: userId,
      });
      return response.data;
    },
    
    getBlocks: async (): Promise<SwipesResponse> => {
      const response = await apiClient.get('/user/blocks');
      return response.data;
    },
  },
  
  // Creators
  creators: {
    getByRole: async (role?: string, limit?: number): Promise<{ data: User[] }> => {
      const response = await apiClient.get('/creators', {
        params: { role, limit },
      });
      return response.data;
    },
  },
  
  // Purchases
  purchase: {
    create: async (data: PurchaseRequest): Promise<PurchaseResponse> => {
      const response = await apiClient.post('/purchase/create', data);
      return response.data;
    },
  },
  
  // Uploads (for creators)
  upload: {
    init: async (fileName: string, size: number): Promise<{ upload_id: string }> => {
      const response = await apiClient.post('/uploads/init', {
        file_name: fileName,
        size,
      });
      return response.data;
    },
    
    uploadPart: async (
      uploadId: string,
      partNumber: number,
      data: Blob
    ): Promise<{ ok: boolean }> => {
      const formData = new FormData();
      formData.append('file', data);
      
      const response = await apiClient.put(
        `/uploads/${uploadId}/part`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: { part_number: partNumber },
        }
      );
      return response.data;
    },
    
    complete: async (
      uploadId: string,
      metadata: Record<string, unknown>
    ): Promise<{ ok: boolean; url: string | null }> => {
      const response = await apiClient.post(
        `/uploads/${uploadId}/complete`,
        metadata
      );
      return response.data;
    },
  },
  
  // Profile
  profile: {
    get: async (): Promise<UserProfile> => {
      const response = await apiClient.get('/profile');
      return response.data;
    },
    update: async (
      data: Partial<UserProfile> & {
        roles?: string;
        name?: string;
        city?: string;
        styles?: string;
        bio?: string;
        photo_file_id?: string;
        contact_telegram_mode?: ContactTelegramMode;
        contact_telegram_custom?: string | null;
        contact_telegram_privacy?: ContactTelegramPrivacy;
        allow_chat_messages?: boolean;
      }
    ): Promise<{ ok: boolean }> => {
      const response = await apiClient.post('/profile', data);
      return response.data;
    },
    updatePortfolio: async (items: PortfolioItem[]): Promise<{ ok: boolean }> => {
      const response = await apiClient.post('/profile/portfolio', { portfolios: items });
      return response.data;
    },
    uploadPortfolioFile: async (file: File, caption: string): Promise<UploadPortfolioFileResponse> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('caption', caption);

      const response = await apiClient.post('/upload_portfolio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    delete: async (): Promise<{ ok: boolean }> => {
      const response = await apiClient.delete('/profile');
      return response.data;
    },
  },
  
  // Referral
  referral: {
    get: async (): Promise<{ link: string; count: number }> => {
      const response = await apiClient.get('/referral');
      return response.data;
    },
  },
  
  // Boost
  boost: {
    status: async (): Promise<{ is_boosted: boolean; boost_expires_at?: string | null }> => {
      const response = await apiClient.get('/boost/status');
      return response.data;
    },
  },

  // KYC verification (simple stub integration)
  kyc: {
    start: async (userId: number | string): Promise<KycVerification> => {
      const response = await apiClient.post('/verifications/start', {
        user_id: String(userId),
      });
      return response.data;
    },

    getLatest: async (userId: number | string): Promise<KycVerification> => {
      const response = await apiClient.get('/verifications/latest', {
        params: { user_id: String(userId) },
      });
      return response.data;
    },

    uploadDocument: async (verificationId: string, file: File): Promise<KycVerification> => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(`/verifications/${verificationId}/document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },

    uploadSelfie: async (verificationId: string, file: File): Promise<KycVerification> => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(`/verifications/${verificationId}/selfie`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },

    submit: async (verificationId: string): Promise<KycVerification> => {
      const response = await apiClient.post(`/verifications/${verificationId}/submit`);
      return response.data;
    },
  },
  
  // Link preview
  linkPreview: {
    get: async (url: string): Promise<{ url: string; title?: string; description?: string; image?: string; site_name?: string; error?: string }> => {
      const response = await apiClient.get('/link-preview', { params: { url } });
      return response.data;
    },
  },
  
  // Chat
  chat: {
    getChats: async (): Promise<{
      data: Array<{
        id: number;
        name: string;
        username?: string;
        photo_url?: string;
        last_message?: string;
        last_message_at?: string;
        unread_count: number;
        i_liked_them: boolean;
        they_liked_me: boolean;
      }>;
    }> => {
      const response = await apiClient.get('/chats');
      return response.data;
    },
    
    getMessages: async (
      otherUserId: number,
      limit?: number,
      offset?: number
    ): Promise<{
      data: Array<{
        id: number;
        text: string;
        is_from_me: boolean;
        created_at: string;
        read_at?: string | null;
      }>;
    }> => {
      const response = await apiClient.get(`/chats/${otherUserId}/messages`, {
        params: { limit, offset },
      });
      return response.data;
    },
    
    sendMessage: async (
      otherUserId: number,
      text: string
    ): Promise<{
      ok: boolean;
      message: {
        id: number;
        text: string;
        is_from_me: boolean;
        created_at: string;
        read_at?: string | null;
      };
    }> => {
      const response = await apiClient.post(`/chats/${otherUserId}/messages`, {
        to_user_id: otherUserId,
        text,
      });
      return response.data;
    },

    getChatApproval: async (otherUserId: number): Promise<ChatApprovalStatus> => {
      const response = await apiClient.get(`/chats/${otherUserId}/approval`);
      return response.data;
    },

    setChatApproval: async (
      otherUserId: number,
      approved: boolean
    ): Promise<{ ok: boolean; approved: boolean }> => {
      const response = await apiClient.post(`/chats/${otherUserId}/approval`, {
        approved,
      });
      return response.data;
    },
  },
};

// ---- Mock API for Developer Mode ----
function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const mockApi: typeof realApi = {
  auth: {
    init: async (): Promise<AuthResponse> => {
      await sleep(200);
      const token = 'dev-token';
      try { localStorage.setItem('splyt_token', token); } catch {}
      return { ok: true, token, user: devUser };
    },
  },
  swipes: {
    getForYou: async (
      _telegramId: number,
      _options?: {
        limit?: number;
        filterRoles?: string[];
        filterCity?: string;
        filterStyles?: string[];
      }
    ): Promise<SwipesResponse> => {
      await sleep(150);
      const candidates: CandidateProfile[] = mockItems.slice(0, 20).map((item) => ({
        telegram_id: Number(item.creator_id) || Number(item.id) || 0,
        username: item.creator?.username,
        name: item.creator?.name || item.title,
        roles: [String(item.category || 'creator')],
        roles_display: [String(item.category || 'creator')],
        city: 'Київ',
        styles: item.tags?.map(String) || [],
        bio: item.description,
        link: undefined,
        photo_url: item.creator?.avatar || item.cover_url,
        photo_file_id: item.creator?.avatar || null,
        is_boosted: false,
        portfolios: [],
        elo_rating: 1500,
        total_likes_received: 0,
        total_matches: 0,
        last_activity_at: new Date().toISOString(),
      }));
      return { data: candidates, swipes_used: 0, swipes_remaining: 99 };
    },
    like: async (
      _fromUserId: number,
      _toUserId: number
    ): Promise<{ ok: boolean; is_match: boolean; swipes_used: number; swipes_remaining: number }> => {
      await sleep(80);
      return { ok: true, is_match: Math.random() > 0.7, swipes_used: 1, swipes_remaining: 98 };
    },
    skip: async (
      _fromUserId: number,
      _toUserId: number
    ): Promise<{ ok: boolean; swipes_used: number; swipes_remaining: number }> => {
      await sleep(60);
      return { ok: true, swipes_used: 1, swipes_remaining: 98 };
    },
  },
  catalog: {
    getItems: async (params?: { category?: string; sort?: string; cursor?: string; filters?: Record<string, unknown>; }): Promise<CatalogResponse> => {
      await sleep(180);
      let list = mockItems;
      const category = (params?.category as string | undefined) || (params?.filters as Record<string, unknown>)?.category;
      if (category && category !== 'all') {
        list = list.filter((i) => i.category === category);
      }
      if ((params?.filters as Record<string, unknown>)?.price_type === 'free') list = list.filter((i) => i.price_uah === 0);
      if ((params?.filters as Record<string, unknown>)?.genre) {
        const g = String((params?.filters as Record<string, unknown>).genre).toLowerCase();
        list = list.filter((i) => i.tags?.some((t) => t.toLowerCase() === g));
      }
      if (params?.sort === 'popular') list = [...list].sort((a, b) => (b.stats?.plays || 0) - (a.stats?.plays || 0));
      if (params?.sort === 'trending') list = [...list].sort((a, b) => (b.stats?.likes || 0) - (a.stats?.likes || 0));
      if (params?.sort === 'newest') list = [...list].sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
      return { data: list.slice(0, 50), total: list.length, page: 1, per_page: 50 };
    },
    getItem: async (id: string): Promise<Item> => {
      await sleep(120);
      const item = mockItems.find((i) => i.id === id) || mockItems[0];
      return item;
    },
    create: async (data: CreateWorkData): Promise<CreateWorkResponse> => {
      await sleep(200);
      console.log('Mock create catalog item', data);
      return { status: 'ok', item_id: Math.floor(Math.random() * 100000) };
    },
  },
  market: {
    getProfile: async (userId: number): Promise<MarketProfile> => {
      await sleep(120);

      const itemsForCreator = mockItems.filter((item) => Number(item.creator_id) === Number(userId));
      const fallbackItem = itemsForCreator[0] || mockItems[0];
      const creatorName = fallbackItem?.creator?.name || 'Mock Creator';
      const creatorUsername = fallbackItem?.creator?.username || 'mock_creator';

      const marketplacePortfolios: PortfolioItem[] = (itemsForCreator.length ? itemsForCreator : mockItems.slice(0, 6)).slice(0, 6).map((item) => ({
        type: 'audio',
        source: 'marketplace',
        value: item.preview_url,
        title: item.title,
        cover_url: item.cover_url,
        item_id: item.id,
        creator_id: item.creator_id,
      }));

      const profile: MarketProfile = {
        telegram_id: userId,
        username: creatorUsername,
        name: creatorName,
        roles: 'beatmaker', // default role
        city: 'Kyiv', // default city
        styles: 'Trap, Hip-Hop', // default styles
        bio: 'This is a mock bio for a talented creator.',
        portfolios: marketplacePortfolios,
        stats: {
          marketplace_items: itemsForCreator.length,
          portfolio_items: 0,
          total_likes: 123,
          profile_likes: 45,
          item_likes: 78,
          followers: 100,
        },
      };

      return profile;
    },
  },
  search: {
    query: async (q: string, filters?: Record<string, unknown>): Promise<{
      data: Item[];
      corrected_query?: string;
      original_query: string;
      related_queries: string[];
      similar_items: Item[];
    }> => {
      await sleep(100);
      return searchMock(q, filters);
    },
    suggestions: async (q:string): Promise<{ suggestions: string[] }> => {
      await sleep(50);
      const suggestions = ['lo-fi', 'phonk', 'ambient', 'type beat', 'guitar', 'piano'];
      return { suggestions: suggestions.filter(s => s.startsWith(q.toLowerCase())) };
    },
  },
  similar: {
    get: async (itemId: string, limit?: number): Promise<CatalogResponse> => {
      await sleep(120);
      const { data, total, page, per_page } = getSimilarItems(itemId, limit);
      return { data, total, page, per_page };
    },
  },
  recommendations: {
    get: async (limit?: number): Promise<{
      data: Item[];
      cached: boolean;
      total: number;
    }> => {
      await sleep(150);
      const recs = mockItems.slice(0, limit || 10);
      return {
        data: recs,
        cached: false,
        total: recs.length,
      };
    },
  },
  analytics: {
    recordInteraction: async (payload: RecordInteractionPayload): Promise<{ status: string }> => {
      console.log('Analytics interaction recorded:', payload);
      await sleep(30);
      return { status: 'ok' };
    },
  },
  user: {
    follow: async (userId: number): Promise<{ ok: boolean }> => {
      console.log(`Followed user ${userId}`);
      await sleep(50);
      return { ok: true };
    },
    unfollow: async (userId: number): Promise<{ ok: boolean }> => {
      console.log(`Unfollowed user ${userId}`);
      await sleep(50);
      return { ok: true };
    },
    getFollowStatus: async (userId: number): Promise<{ is_following: boolean }> => {
      console.log(`Checked follow status for user ${userId}`);
      await sleep(40);
      return { is_following: Math.random() > 0.5 };
    },
    favorite: async (itemId: string): Promise<{ ok: boolean; favorited: boolean }> => {
      console.log(`Favorited item ${itemId}`);
      await sleep(50);
      const favorited = Math.random() > 0.5;
      return { ok: true, favorited };
    },
    getFavorites: async (): Promise<Item[]> => {
      await sleep(100);
      return mockItems.slice(0, 5);
    },
    getDashboard: async (): Promise<unknown> => {
      await sleep(120);
      return {
        stats: {
          plays: 1234,
          likes: 567,
          followers: 89,
        },
        recent_activity: [],
      };
    },
    getPurchases: async (): Promise<UserPurchase[]> => {
      await sleep(100);
      return [
        {
          id: 1,
          item_id: '1',
          format: 'mp3',
          price_paid: 100,
          status: 'completed',
          created_at: new Date().toISOString(),
          title: 'Awesome Beat',
          cover_url: 'https://via.placeholder.com/150',
          files: [{ url: '#', name: 'awesome_beat.mp3' }],
        },
      ];
    },
    block: async (userId: number): Promise<{ ok: boolean }> => {
      console.log(`Blocked user ${userId}`);
      await sleep(50);
      return { ok: true };
    },
    unblock: async (userId: number): Promise<{ ok: boolean }> => {
      console.log(`Unblocked user ${userId}`);
      await sleep(50);
      return { ok: true };
    },
    getBlocks: async (): Promise<SwipesResponse> => {
      await sleep(80);
      return { data: [], swipes_used: 0, swipes_remaining: 0 };
    },
  },
  creators: {
    getByRole: async (role?: string, limit?: number): Promise<{ data: User[] }> => {
      await sleep(90);
      return getCreatorsByRole(role, limit);
    },
  },
  purchase: {
    create: async (data: PurchaseRequest): Promise<PurchaseResponse> => {
      console.log('Creating purchase:', data);
      await sleep(150);
      return {
        ok: true,
        invoice_id: `inv_${Math.random()}`,
        telegram_invoice_params: {},
      };
    },
  },
  upload: {
    init: async (fileName: string, size: number): Promise<{ upload_id: string }> => {
      console.log(`Upload init: ${fileName} (${size} bytes)`);
      await sleep(50);
      return { upload_id: `upload_${Math.random()}` };
    },
    uploadPart: async (uploadId: string, partNumber: number, data: Blob): Promise<{ ok: boolean }> => {
      console.log(`Uploading part ${partNumber} for ${uploadId}, size: ${data.size}`);
      await sleep(100);
      return { ok: true };
    },
    complete: async (uploadId: string, metadata: Record<string, unknown>): Promise<{ ok: boolean; url: string | null }> => {
      console.log(`Upload complete for ${uploadId}`, metadata);
      await sleep(50);
      return { ok: true, url: 'https://mock-storage.com/file.mp3' };
    },
  },
  profile: {
    get: async (): Promise<UserProfile> => {
      await sleep(80);
      return {
        telegram_id: 1,
        username: 'devuser',
        name: 'Dev User',
        roles: 'listener,beatmaker',
        city: 'Kyiv',
        styles: 'lo-fi,phonk',
        bio: 'I make beats.',
        portfolios: [],
      };
    },
    update: async (data: Partial<UserProfile>): Promise<{ ok: boolean }> => {
      console.log('Profile updated:', data);
      await sleep(100);
      return { ok: true };
    },
    updatePortfolio: async (items: PortfolioItem[]): Promise<{ ok:boolean }> => {
      console.log('Portfolio updated:', items);
      await sleep(100);
      return { ok: true };
    },
    uploadPortfolioFile: async (file: File, caption: string): Promise<UploadPortfolioFileResponse> => {
      console.log('Uploading portfolio file:', file.name, caption);
      await sleep(150);
      return {
        url: 'https://mock-storage.com/portfolio-item.jpg',
        caption,
        type: 'photo',
      };
    },
    delete: async (): Promise<{ ok: boolean }> => {
      console.log('Profile deleted');
      await sleep(200);
      return { ok: true };
    },
  },
  referral: {
    get: async (): Promise<{ link: string; count: number; }> => {
      await sleep(60);
      return { link: 'https://t.me/splyt?start=ref123', count: 5 };
    },
  },
  boost: {
    status: async (): Promise<{ is_boosted: boolean; boost_expires_at?: string | null; }> => {
      await sleep(50);
      return { is_boosted: true, boost_expires_at: new Date(Date.now() + 86400000).toISOString() };
    },
  },
  kyc: {
    start: async (userId: number | string): Promise<KycVerification> => {
      await sleep(100);
      return {
        id: `kyc_${userId}`,
        user_id: String(userId),
        status: 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        has_document_image: false,
        has_selfie_image: false,
      };
    },
    getLatest: async (userId: number | string): Promise<KycVerification> => {
      await sleep(80);
      const k: KycVerification = {
        id: `kyc_${userId}`,
        user_id: String(userId),
        status: 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        has_document_image: true,
        has_selfie_image: false,
      };
      return k;
    },
    uploadDocument: async (verificationId: string, file: File): Promise<KycVerification> => {
      console.log(`Uploading document for KYC ${verificationId}:`, file.name);
      await sleep(200);
      const kyc: KycVerification = await mockApi.kyc.getLatest('1');
      kyc.has_document_image = true;
      return kyc;
    },
    uploadSelfie: async (verificationId: string, file: File): Promise<KycVerification> => {
      console.log(`Uploading selfie for KYC ${verificationId}:`, file.name);
      await sleep(200);
      const kyc: KycVerification = await mockApi.kyc.getLatest('1');
      kyc.has_selfie_image = true;
      return kyc;
    },
    submit: async (verificationId: string): Promise<KycVerification> => {
      console.log(`Submitting KYC ${verificationId}`);
      await sleep(100);
      const kyc: KycVerification = await mockApi.kyc.getLatest('1');
      kyc.status = Math.random() > 0.5 ? 'verified' : 'rejected';
      kyc.reason = kyc.status === 'rejected' ? 'Poor image quality' : null;
      return kyc;
    },
  },
  linkPreview: {
    get: async (url: string): Promise<{ url: string; title?: string; description?: string; image?: string; site_name?: string; error?: string }> => {
      await sleep(150);
      if (url.includes('spotify')) {
        return {
          url,
          title: 'Song on Spotify',
          description: 'A great track for your playlist.',
          image: 'https://via.placeholder.com/150',
          site_name: 'Spotify',
        };
      }
      return { url, title: 'Link Preview', description: 'This is a preview of the link.' };
    },
  },
  chat: {
    getChats: async (): Promise<{
      data: Array<{
        id: number;
        name: string;
        username?: string;
        photo_url?: string;
        last_message?: string;
        last_message_at?: string;
        unread_count: number;
        i_liked_them: boolean;
        they_liked_me: boolean;
      }>;
    }> => {
      await sleep(100);
      return { data: [
        { id: 2, name: 'Alice', last_message: 'Hey!', unread_count: 1, i_liked_them: true, they_liked_me: true, photo_url: 'https://i.pravatar.cc/150?u=alice' },
        { id: 3, name: 'Bob', last_message: 'See you later.', unread_count: 0, i_liked_them: true, they_liked_me: false, photo_url: 'https://i.pravatar.cc/150?u=bob' },
      ] };
    },
    getMessages: async (otherUserId: number, limit?: number, offset?: number): Promise<{
      data: Array<{
        id: number;
        text: string;
        is_from_me: boolean;
        created_at: string;
        read_at?: string | null;
      }>;
    }> => {
      await sleep(80);
      console.log('Mock getMessages:', { otherUserId, limit, offset });
      return { data: [
        { id: 1, text: 'Hello', is_from_me: false, created_at: new Date().toISOString() },
        { id: 2, text: 'Hi there', is_from_me: true, created_at: new Date().toISOString() },
      ] };
    },
    sendMessage: async (otherUserId: number, text: string): Promise<{
      ok: boolean;
      message: {
        id: number;
        text: string;
        is_from_me: boolean;
        created_at: string;
        read_at?: string | null;
      };
    }> => {
      await sleep(60);
      console.log('Mock sendMessage:', { otherUserId, text });
      return { ok: true, message: { id: 3, text, is_from_me: true, created_at: new Date().toISOString() } };
    },
    getChatApproval: async (otherUserId: number): Promise<ChatApprovalStatus> => {
      await sleep(50);
      console.log('Mock getChatApproval:', { otherUserId });
      return { mode: 'manual', approved: true };
    },
    setChatApproval: async (otherUserId: number, approved: boolean): Promise<{ ok: boolean, approved: boolean }> => {
      await sleep(70);
      console.log('Mock setChatApproval:', { otherUserId, approved });
      return { ok: true, approved };
    },
  },
};

export const api = isDevModeEnabled() ? mockApi : realApi;
